import React, { Component } from 'react';

import './Map.css';

import MediaBar from '../Controls/MediaBar.js';
import VideoSync from './VideoSync.js';
import VideoZoomPopup from './VideoZoomPopup.js';

class Map extends Component {

    constructor(props) {
        super(props);

        this.state = {
            isMapLoaded: false,
            videoHidden: true,
            convertedMapList: "",
            recordingFile: "", //the open recording; the zoom popup's frame service needs it

            player1src: "",
            player2src: "",
            player3src: "",
            player4src: "",
            player1srcExists: false,
            player2srcExists: false,
            player3srcExists: false,
            player4srcExists: false,
            videoActive: false
        };

        //plain html5 <video> elements (video-react's Player mirrored every state change
        //into React state several times a second per player, which is what froze the map).
        //Playback state lives in the elements; nothing here re-renders per frame.
        this.videoRefs = [React.createRef(), React.createRef(), React.createRef(), React.createRef()];

        //the sync group is the videos that actually have a camera source
        this.sync = new VideoSync(() => {
            const exists = [this.state.player1srcExists, this.state.player2srcExists,
                            this.state.player3srcExists, this.state.player4srcExists];
            return this.videoRefs.filter((ref, i) => exists[i] && ref.current).map(ref => ref.current);
        });

        this.mediaInterval = null;
        this.zoomPopup = React.createRef();
    }

    //called when object is first mounted
    componentDidMount() {

        const script = document.createElement("script");

        script.src = "createMap.js";
        script.async = true;

        document.body.appendChild(script);

        //the potree glue (potreeMap.js) calls this when the user clicks a *_traj cloud:
        //the value is seconds since the trajectory's first point, i.e. into the recording.
        //The videos land on that moment paused, for inspecting the frame.
        window.orbisFuseSeekVideos = (seconds) => { this.seekVideosToSeconds(seconds, false); };
    }

    componentWillUnmount() {
        this.stopBarUpdates();
        delete window.orbisFuseSeekVideos;
    }

    openMap = (file) => {

        fetch('recording_info', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: "recording=" + file
        })
        .then(res => res.json())
        .then((data) => {

            this.setState({
                convertedMapList: data[0].convertedMapList,
                recordingFile: file
            });

            const script = document.createElement("script");

            script.src = "loadMap.js";
            script.async = true;
            script.setAttribute("convertedMapList", data[0].convertedMapList);
            script.setAttribute("mapFile", file);

            document.body.appendChild(script);

            this.setState({
                isMapLoaded: true
            });

            var p1src = "maps/" + file + "/" + data[0].camera0;
            var p2src = "maps/" + file + "/" + data[0].camera1;
            var p3src = "maps/" + file + "/" + data[0].camera2;
            var p4src = "maps/" + file + "/" + data[0].camera3;
            var p1srcExists = (data[0].camera0.length > 0);
            var p2srcExists = (data[0].camera1.length > 0);
            var p3srcExists = (data[0].camera2.length > 0);
            var p4srcExists = (data[0].camera3.length > 0);
            var videoActive = p1srcExists || p2srcExists || p3srcExists || p4srcExists;

            this.setState({
                player1src: p1src,
                player2src: p2src,
                player3src: p3src,
                player4src: p4src,
                player1srcExists: p1srcExists,
                player2srcExists: p2srcExists,
                player3srcExists: p3srcExists,
                player4srcExists: p4srcExists,
                videoActive: videoActive
            }, () => {
                //reload the video elements onto their new sources
                this.videoRefs.forEach((ref) => {
                    if (ref.current && ref.current.getAttribute('src'))
                    {
                        ref.current.load();
                    }
                });
            });
        })
        .catch(console.log)
    }

    openAtlas = descriptor => {
        this.stopBarUpdates();
        this.videoRefs.forEach(ref => { if (ref.current) ref.current.pause(); });
        this.setState({ convertedMapList: descriptor.convertedMapList, recordingFile: descriptor.atlasName,
            isMapLoaded: true, videoActive: false,
            player1src: '', player2src: '', player3src: '', player4src: '',
            player1srcExists: false, player2srcExists: false, player3srcExists: false, player4srcExists: false });
        const script = document.createElement('script');
        script.src = 'loadMap.js';
        script.async = true;
        script.setAttribute('convertedMapList', descriptor.convertedMapList);
        script.setAttribute('mapFile', descriptor.atlasName);
        script.setAttribute('sourceKind', 'atlas');
        document.body.appendChild(script);
    }

    resetMap = () => {

        this.setState({
            isMapLoaded: false
        });
    }

    //---- playback control: the media bar is the master control, the four videos follow ----

    startBarUpdates() {

        if (null == this.mediaInterval)
        {
            this.mediaInterval = setInterval(() => { this.updateMediaBar() }, 1000);
        }
    }

    stopBarUpdates() {

        if (null != this.mediaInterval)
        {
            clearInterval(this.mediaInterval);
            this.mediaInterval = null;
        }
    }

    updateMediaBar() {

        const duration = this.sync.duration();
        const currentTime = this.sync.currentTime();

        this.mediaBar.setValue(duration > 0 ? 100.0 * currentTime / duration : 0, currentTime, duration);

        //keep the followers on the master's clock while playing
        this.sync.correctDrift();
    }

    mediaBarChangeHandler(value)
    {
        this.seekVideosToSeconds(0.01 * value * this.sync.duration(), true);
    }

    //jump the synchronised video group to an absolute time (seconds into the recording).
    //A media bar seek resumes playback there (startPlaying true); a trajectory click on the
    //map lands the videos on that moment paused (startPlaying false).
    seekVideosToSeconds(seconds, startPlaying)
    {
        console.log("TrajClick: seeking videos to " + seconds + "s (video duration " + this.sync.duration() + "s, startPlaying " + startPlaying + ")");

        const duration = this.sync.duration();
        let seekTime = Math.max(0, seconds);

        if (duration > 0 && seekTime > duration)
        {
            seekTime = duration;
        }

        if (this.zoomPopup.current && this.zoomPopup.current.isOpen())
        {
            //frame-inspection mode: scrub the paused group (popup frame included) without
            //resuming, and the popup stays open
            this.sync.seekAll(seekTime).then(() => {
                this.updateMediaBar();
            });

            this.zoomPopup.current.seek(seekTime);
        }
        else if (startPlaying)
        {
            //land every video on the new time first, then start them together
            this.sync.seekAndPlay(seekTime).then(() => {
                this.mediaBar.setPlaying();
                this.startBarUpdates();
                this.updateMediaBar();
            });
        }
        else
        {
            //land every video on the new time and stay there, paused
            this.sync.pauseAll();
            this.stopBarUpdates();
            this.mediaBar.setPaused();

            this.sync.seekAll(seekTime).then(() => {
                this.updateMediaBar();
            });
        }
    }

    mediaBarPauseHandler()
    {
        this.stopBarUpdates();
        this.sync.pauseAll();
        this.mediaBar.setPaused();
    }

    //the media bar's play button: resume the group at its current position; leaving
    //frame-inspection, so the zoom popup closes
    mediaBarPlayHandler()
    {
        if (this.zoomPopup.current && this.zoomPopup.current.isOpen())
        {
            this.zoomPopup.current.hide();
        }

        this.sync.playAll().then(() => {
            this.mediaBar.setPlaying();
            this.startBarUpdates();
        });
    }

    //clicking a camera pauses the group in step and inspects that camera's frame enlarged
    handleVideoClick = (index) => {

        this.sync.pauseAll();
        this.mediaBar.setPaused();
        this.stopBarUpdates();

        if (this.zoomPopup.current)
        {
            const src = this.state['player' + (index + 1) + 'src'];

            //the popup prefers the full-resolution frame from the camera's ts via the
            //camera_frame service, falling back to this mp4 when that is unavailable
            this.zoomPopup.current.show(src, this.sync.currentTime(), {
                recording: this.state.recordingFile,
                camera: index
            });
        }
    }

    handleVideoEnded = () => {
        this.sync.pauseAll();
        this.mediaBar.setPaused();
        this.stopBarUpdates();
    }

    hideMediaBar()
    {
        this.setState({
            videoHidden: true
        });
    }

    showMediaBar()
    {
        this.setState({
            videoHidden: false
        });
    }

    renderVideo(index) {

        const src = this.state['player' + (index + 1) + 'src'];
        const srcExists = this.state['player' + (index + 1) + 'srcExists'];
        const visibility = 'video' + (index + 1) + (srcExists ? 'Enabled' : 'Disabled');

        return (
            <video
                key={index}
                className={visibility + " cameraVideo"}
                ref={this.videoRefs[index]}
                src={srcExists ? src : undefined}
                muted
                playsInline
                preload="metadata"
                onClick={() => this.handleVideoClick(index)}
                onEnded={this.handleVideoEnded}
            />
        );
    }

    render() {
        return (
            <div className={this.props.isHidden ? 'potree_container_hidden' : 'potree_container'}>
                <div id="potree_render_area">
                    <div id="potree_toolbar" class="potree_toolbar"></div>
                </div>
                <div id="potree_sidebar_container"> </div>
                <div class={this.state.videoActive ? (this.state.videoHidden ? "MediaBarHidden" : "MediaBar") : "MediaBarDisabled"}><MediaBar ref={mediaBar => { this.mediaBar = mediaBar; }} onChange = {(value) => {this.mediaBarChangeHandler(value)}} Pause = {() => {this.mediaBarPauseHandler()}} Play = {() => {this.mediaBarPlayHandler()}} hideBar = {() => {this.hideMediaBar()}} showBar = {() => {this.showMediaBar()}}/></div>
                <div className={this.state.videoActive ? (this.state.videoHidden ? "video_bar_hidden" : "video_bar") : "video_bar_hidden" }>
                    {[0, 1, 2, 3].map(index => this.renderVideo(index))}
                </div>
                <VideoZoomPopup ref={this.zoomPopup} />
            </div>)
    }
}

export default Map;
